import { Injectable, computed, signal } from '@angular/core';
import { LocalStorageService } from 'ngx-webstorage';

/**
 * Holds the FYES gateway session token (minted by our backend at login).
 * The interceptor sends it as the x-fyes-session-token header on every request,
 * and the backend recovers the cloud user id from it — so the client never has
 * to (and must not) send the idUser itself.
 *
 * Kept separate from the Parse `session_token` used by the dashboard.
 */
@Injectable({ providedIn: 'root' })
export class SessionService {
    private static readonly TOKEN_KEY = 'fyes_session_token';

    private readonly _token = signal<string | null>(null);
    readonly isLoggedIn = computed(() => this._token() !== null);

    /** Whether the logged-in user is a cloud admin (read from the token). */
    readonly isAdmin = computed(() => !!decodeJwtPayload(this._token())?.isAdmin);

    /**
     * true for a local account (Fyes_AdminUsers), false for a cloud gateway
     * customer. Mirrors the legacy site's `isGW` flag, inverted: local users get
     * the Files portal, gateway customers get Gateways.
     */
    readonly isLocal = computed(() => !!decodeJwtPayload(this._token())?.isLocal);

    /** Username from the token, for attributing actions in the UI. */
    readonly username = computed<string | null>(() => decodeJwtPayload(this._token())?.username ?? null);

    /**
     * May use the sales dashboard (orders + messages) without administration
     * rights. Admins pass implicitly, so this never has to be set alongside
     * isAdmin. Tokens minted before the flag existed lack it and read as false,
     * so their holders re-authenticate to pick it up.
     */
    readonly canAccessOrders = computed(() => {
        const p = decodeJwtPayload(this._token());
        return !!p && (!!p.isAdmin || !!p.canAccessOrders);
    });

    constructor(private localStorage: LocalStorageService) {
        const token = this.localStorage.retrieve(SessionService.TOKEN_KEY);
        if (token) {
            this._token.set(token);
        }
    }

    get token(): string | null {
        return this._token();
    }

    setToken(token: string): void {
        this._token.set(token);
        this.localStorage.store(SessionService.TOKEN_KEY, token);
    }

    clear(): void {
        this._token.set(null);
        this.localStorage.clear(SessionService.TOKEN_KEY);
    }
}

/**
 * Decode a JWT payload for display purposes only (e.g. reading `isAdmin`).
 * The signature is NOT verified here — the backend always re-verifies the token.
 */
function decodeJwtPayload(token: string | null): any | null {
    if (!token) return null;
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
        if (typeof atob === 'undefined') return null;
        return JSON.parse(atob(base64));
    } catch {
        return null;
    }
}
